
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// Imported smaller components
import JobDescriptionInput from "./proposal/JobDescriptionInput";
import ProposalDisplay from "./proposal/ProposalDisplay";
import ErrorDisplay from "./proposal/ErrorDisplay";
import UserProfileInput from "./proposal/UserProfileInput";
import LoginRequiredDialog from "@/components/courses/LoginRequiredDialog";

// Imported utility functions
import { checkRateLimit, setLastRequestTime } from "./proposal/rate-limiting";
import { MIN_WORD_COUNT, countWords } from "./proposal/text-utils";
import { generateProposalApi } from "./proposal/proposal-api";

const ProposalGenerator = () => {
  const [jobDescription, setJobDescription] = useState("");
  const [profileTitle, setProfileTitle] = useState("");
  const [profileOverview, setProfileOverview] = useState("");
  const [proposal, setProposal] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isFallback, setIsFallback] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Handle cooldown countdown
  useEffect(() => {
    let interval: number | null = null;
    
    if (cooldownRemaining > 0) {
      interval = window.setInterval(() => {
        setCooldownRemaining(prev => {
          const newValue = prev - 1000;
          if (newValue <= 0) {
            clearInterval(interval!);
            return 0;
          }
          return newValue;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownRemaining]);

  const handleJobDescriptionChange = (value: string) => {
    setJobDescription(value);
  };

  const handleProfileSubmit = async () => {
    setIsLoading(true);
    setProposal("");
    setError(null);
    setIsQuotaExceeded(false);
    setIsRateLimited(false);
    setIsFallback(false);

    const result = await generateProposalApi(
      jobDescription, 
      profileTitle, 
      profileOverview
    );
    
    setProposal(result.proposal);
    setError(result.error);
    setIsQuotaExceeded(result.isQuotaExceeded);
    setIsRateLimited(result.isRateLimited);
    setIsFallback(result.isFallback);
    
    if (result.isRateLimited) {
      setCooldownRemaining(10000); // 10 seconds
    }
    
    if (result.proposal && !result.error) {
      if (result.isFallback) {
        toast({
          title: "Basic proposal generated",
          description: "A basic fallback proposal was created due to high demand. It may need more customization.",
        });
      } else {
        toast({
          title: "Proposal generated!",
          description: "Your winning proposal is ready.",
        });
      }
    }
    
    setIsLoading(false);
  };

  const handleContinue = () => {
    if (!user) {
      // Show login dialog if not authenticated
      setLoginDialogOpen(true);
      return;
    }

    if (!jobDescription.trim()) {
      toast({
        title: "Job description required",
        description: "Please enter a job description to generate a proposal.",
        variant: "destructive",
      });
      return;
    }

    const wordCount = countWords(jobDescription);
    if (wordCount < MIN_WORD_COUNT) {
      toast({
        title: "Job description too short",
        description: `Please provide a job description with at least ${MIN_WORD_COUNT} words. (Currently: ${wordCount} words)`,
        variant: "destructive",
      });
      return;
    }

    // Client-side rate limiting
    const { isLimited, remainingTime } = checkRateLimit();
    if (isLimited) {
      setIsRateLimited(true);
      setCooldownRemaining(remainingTime);
      const seconds = Math.ceil(remainingTime / 1000);
      toast({
        title: "Please wait",
        description: `You can generate another proposal in ${seconds} seconds`,
        variant: "destructive",
      });
      return;
    }
    
    // Set last request time
    setLastRequestTime();
    
    // Show the profile form
    setShowProfileForm(true);
  };

  const resetForm = () => {
    setShowProfileForm(false);
    setProfileTitle("");
    setProfileOverview("");
  };

  return (
    <div className="space-y-6">
      <ErrorDisplay 
        error={error}
        isQuotaExceeded={isQuotaExceeded}
        isRateLimited={isRateLimited}
        cooldownRemaining={cooldownRemaining}
      />
      
      {!showProfileForm ? (
        <JobDescriptionInput
          jobDescription={jobDescription}
          onJobDescriptionChange={handleJobDescriptionChange}
          onGenerateProposal={handleContinue}
          isLoading={isLoading}
          isRateLimited={isRateLimited}
          cooldownRemaining={cooldownRemaining}
          buttonText="Continue"
        />
      ) : (
        <UserProfileInput
          profileTitle={profileTitle}
          profileOverview={profileOverview}
          onProfileTitleChange={setProfileTitle}
          onProfileOverviewChange={setProfileOverview}
          onSubmit={handleProfileSubmit}
          onBack={resetForm}
          isLoading={isLoading}
        />
      )}

      {proposal && (
        <ProposalDisplay 
          proposal={proposal}
          isFallback={isFallback}
        />
      )}

      <LoginRequiredDialog 
        isOpen={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        redirectUrl="/freelance-toolkit/proposal-generator"
        message="Please log in to use the proposal generator tool."
      />
    </div>
  );
};

export default ProposalGenerator;
